import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { trackEvent } from "../analytics";
import {
  buildWhatsAppLink,
  WHATSAPP_DEFAULT_MSG_EN,
  WHATSAPP_DEFAULT_MSG_ES,
} from "../config";

const WhatsAppIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const WhatsAppFloat = () => {
  const { t, lang } = useLang();
  const [showLabel, setShowLabel] = useState(false);
  const link = buildWhatsAppLink(
    lang === "es" ? WHATSAPP_DEFAULT_MSG_ES : WHATSAPP_DEFAULT_MSG_EN
  );

  // Reveal a small "Chat with us" callout after a short delay on first load
  useEffect(() => {
    const timer = setTimeout(() => setShowLabel(true), 2200);
    const hideTimer = setTimeout(() => setShowLabel(false), 9000);
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] flex items-center gap-3"
      data-testid="whatsapp-float"
    >
      <AnimatePresence>
        {showLabel && (
          <motion.button
            type="button"
            onClick={() => setShowLabel(false)}
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="hidden sm:block rounded-2xl bg-white border border-brand-navy/10 px-4 py-2.5 shadow-[0_15px_30px_-10px_rgba(14,42,86,0.25)] text-left"
            data-testid="whatsapp-callout"
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-brand-red">
              {t.whatsapp.online}
            </div>
            <div className="font-heading font-bold text-sm text-brand-navy leading-tight">
              {t.whatsapp.chatPrompt}
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <motion.a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t.whatsapp.aria}
        data-testid="whatsapp-float-btn"
        onClick={() => trackEvent("click_whatsapp", { location: "float" })}
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.5, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="relative inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] text-white shadow-[0_15px_30px_-8px_rgba(37,211,102,0.6)] hover:bg-[#1faa50] transition-colors"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping" aria-hidden="true" />
        <span className="relative">
          <WhatsAppIcon size={28} />
        </span>
      </motion.a>
    </div>
  );
};

export default WhatsAppFloat;
